import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MoveHorizontal } from 'lucide-react';

/**
 * Interactive Before/After image comparison slider.
 *
 *   - One container shows TWO perfectly-aligned images stacked.
 *   - The "after" image is clipped by a vertical reveal whose width
 *     follows the user's drag handle position.
 *   - Works with mouse, touch and keyboard (arrow keys, home/end).
 *   - Auto-demo: gently animates from 50 -> 75 -> 25 -> 50 on first
 *     mount so visitors immediately see the effect; auto-demo stops
 *     on first user interaction.
 *
 * Drop-in: <BeforeAfterSlider />
 */

const BEFORE_SRC = '/brand/slideshow/before.png';
const AFTER_SRC = '/brand/slideshow/after.png';

const DEMO_SEQUENCE = [50, 70, 30, 55];   // % positions
const DEMO_STEP_MS = 1100;

export default function BeforeAfterSlider({ compact = false, title, subtitle, caption }) {
  const [pos, setPos] = useState(50);          // 0..100, position of the divider
  const [dragging, setDragging] = useState(false);
  const [demoActive, setDemoActive] = useState(true);

  const wrapperRef = useRef(null);
  const handleRef = useRef(null);

  // ----- Auto demo on first mount -----
  useEffect(() => {
    if (!demoActive) return undefined;
    let step = 0;
    const tick = () => {
      if (!demoActive) return;
      step = (step + 1) % DEMO_SEQUENCE.length;
      setPos(DEMO_SEQUENCE[step]);
    };
    const id = setInterval(tick, DEMO_STEP_MS);
    return () => clearInterval(id);
  }, [demoActive]);

  const stopDemo = useCallback(() => {
    if (demoActive) setDemoActive(false);
  }, [demoActive]);

  // ----- Pointer handling -----
  const updateFromClientX = useCallback((clientX) => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(pct);
  }, []);

  const onPointerDown = (e) => {
    stopDemo();
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // ----- Click anywhere on the strip to jump there -----
  const onWrapperClick = (e) => {
    if (dragging) return;
    stopDemo();
    updateFromClientX(e.clientX);
  };

  // ----- Keyboard accessibility -----
  const onKeyDown = (e) => {
    const step = e.shiftKey ? 10 : 4;
    if (e.key === 'ArrowLeft')  { e.preventDefault(); stopDemo(); setPos((p) => Math.max(0, p - step)); }
    if (e.key === 'ArrowRight') { e.preventDefault(); stopDemo(); setPos((p) => Math.min(100, p + step)); }
    if (e.key === 'Home')       { e.preventDefault(); stopDemo(); setPos(0); }
    if (e.key === 'End')        { e.preventDefault(); stopDemo(); setPos(100); }
    if (e.key === 'PageDown' || e.key === 'PageUp') { e.preventDefault(); stopDemo(); setPos(50); }
  };

  const viewportInner = (
    <div
      ref={wrapperRef}
      className={`relative w-full rounded-3xl overflow-hidden bg-slate-900 uh-shadow-lg border border-slate-200 select-none ${
        dragging ? 'cursor-grabbing' : 'cursor-ew-resize'
      }`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={onWrapperClick}
      data-testid="before-after-viewport"
      aria-roledescription="image comparison slider"
    >
      <div className={`relative w-full ${compact ? 'aspect-[16/10]' : 'aspect-[4/3] sm:aspect-[16/10]'}`}>
        <img
          src={BEFORE_SRC}
          alt="Beschädigtes Fahrzeug vor der Bearbeitung durch UnfallHeld"
          className="absolute inset-0 h-full w-full object-cover"
          draggable="false"
          loading="eager"
        />

        <div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
          aria-hidden="true"
        >
          <img
            src={AFTER_SRC}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            draggable="false"
            loading="eager"
          />
        </div>

        <div className={`absolute ${compact ? 'top-2 left-2' : 'top-3 left-3 sm:top-4 sm:left-4'} z-10`}>
          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-red-600 text-white text-[10.5px] font-semibold uppercase tracking-[0.16em] shadow-md">
            Schaden
          </span>
        </div>
        <div className={`absolute ${compact ? 'top-2 right-2' : 'top-3 right-3 sm:top-4 sm:right-4'} z-10`}>
          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-emerald-600 text-white text-[10.5px] font-semibold uppercase tracking-[0.16em] shadow-md">
            L&ouml;sung
          </span>
        </div>

        <div
          className="absolute inset-y-0 z-20 pointer-events-none"
          style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
          aria-hidden="true"
        >
          <div className="h-full w-[3px] bg-white shadow-[0_0_18px_rgba(255,255,255,0.6)]" />
        </div>

        <button
          ref={handleRef}
          type="button"
          onKeyDown={onKeyDown}
          className={`absolute z-30 top-1/2 -translate-y-1/2 -translate-x-1/2 inline-flex items-center justify-center ${
            compact ? 'h-10 w-10' : 'h-12 w-12'
          } rounded-full bg-white text-[hsl(var(--primary))] shadow-[0_8px_24px_-6px_rgba(15,23,42,0.45)] border border-white/80 ring-4 ring-white/30 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[hsl(var(--accent))]/50 transition-transform ${
            dragging ? 'scale-110' : ''
          }`}
          style={{ left: `${pos}%` }}
          aria-label="Schieberegler ziehen, um Vorher und Nachher zu vergleichen"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          role="slider"
          data-testid="before-after-handle"
          tabIndex={0}
        >
          <MoveHorizontal className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        </button>

        {demoActive && (
          <div
            className="absolute z-20 top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 mt-16 px-3 py-1.5 rounded-full bg-black/65 text-white text-[11px] font-medium tracking-wide backdrop-blur pointer-events-none transition-opacity"
            data-testid="before-after-hint"
          >
            &larr; Ziehen Sie den Regler &rarr;
          </div>
        )}
      </div>
    </div>
  );

  // Compact variant: no section heading, no quick-buttons, no CTA - just the slider with a soft caption above.
  if (compact) {
    return (
      <div className="space-y-3" data-testid="before-after-compact">
        {(title || subtitle) && (
          <div>
            {title && (
              <div className="font-heading font-semibold text-[hsl(var(--primary))] text-base">
                {title}
              </div>
            )}
            {subtitle && (
              <p className="text-xs text-slate-600 mt-0.5">{subtitle}</p>
            )}
          </div>
        )}
        {viewportInner}
        {caption && (
          <p className="text-[11px] text-slate-500 text-center italic">{caption}</p>
        )}
      </div>
    );
  }

  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16"
      aria-labelledby="before-after-heading"
      data-testid="before-after-section"
    >
      <div className="flex items-end justify-between flex-wrap gap-2 mb-6">
        <div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-slate-200 text-[11px] font-medium text-[hsl(var(--primary))] uh-shadow-sm">
            Vom Schaden zur L&ouml;sung
          </span>
          <h2
            id="before-after-heading"
            className="mt-3 font-heading font-semibold text-2xl sm:text-3xl text-[hsl(var(--primary))]"
          >
            Ziehen Sie den Regler &mdash; und sehen Sie den Unterschied.
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Mit UnfallHeld wird aus einem beschädigten Fahrzeug wieder Ihr vertrauter Begleiter
            &mdash; strukturiert, professionell und ohne Stress.
          </p>
        </div>
        <Link
          to="/unfall-melden"
          className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]"
          data-testid="before-after-cta-link"
        >
          Jetzt Unfall melden <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {viewportInner}

      <div className="mt-4 flex items-center justify-center gap-2 text-xs">
        <button
          type="button"
          onClick={() => { stopDemo(); setPos(100); }}
          className="px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors font-medium"
          data-testid="before-after-show-before"
        >
          Nur Schaden zeigen
        </button>
        <button
          type="button"
          onClick={() => { stopDemo(); setPos(50); }}
          className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition-colors font-medium"
          data-testid="before-after-show-50"
        >
          50 / 50
        </button>
        <button
          type="button"
          onClick={() => { stopDemo(); setPos(0); }}
          className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors font-medium"
          data-testid="before-after-show-after"
        >
          Nur L&ouml;sung zeigen
        </button>
      </div>

      <div className="mt-6 sm:hidden">
        <Link
          to="/unfall-melden"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-[hsl(var(--primary))] text-white font-semibold"
          data-testid="before-after-cta-mobile"
        >
          Jetzt Unfall melden <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}
